/**
 * Split `/insights/errors` into pipeline errors and single-site errors (tina spec §10.2).
 *
 * Over the seven days to 2026-09-23, 547 of ~555 unrecovered errors were `discover`/`fetch`
 * errors of single sites — the Site health page's job, not this one. They are hidden here unless
 * one error type hits OUTAGE_MIN_SITES sites on one day: over 45 days a normal day had 1-3, and the
 * worst, 11 (`fetch` `JinaFetchError`, 2026-09-07), was Jina's problem, not a site's.
 */
import type { ErrorBucketsRow } from "@/types/tina";

export const SITE_STEPS: ReadonlySet<string> = new Set(["discover", "fetch"]);
export const OUTAGE_MIN_SITES = 10;

export function isSiteStep(stage: string): boolean {
  return SITE_STEPS.has(stage);
}

export interface SiteErrorSplit {
  pipelineRows: ErrorBucketsRow[];
  hiddenSiteErrors: number;
  outages: Map<string, { day: string; siteCount: number }>;
}

export function splitSiteErrors(
  rows: ErrorBucketsRow[],
  { includeRecovered = false, minSites = OUTAGE_MIN_SITES }: { includeRecovered?: boolean; minSites?: number } = {},
): SiteErrorSplit {
  const outages = new Map<string, { day: string; siteCount: number }>();
  for (const r of rows) {
    if (!isSiteStep(r.stage) || r.recovered) continue;
    const sites = r.site_count ?? 0;
    if (sites < minSites) continue;
    const key = `${r.stage}::${r.error_type}`;
    const current = outages.get(key);
    if (!current || sites > current.siteCount) outages.set(key, { day: r.day, siteCount: sites });
  }
  let hiddenSiteErrors = 0;
  const pipelineRows = rows.filter((r) => {
    if (!isSiteStep(r.stage) || outages.has(`${r.stage}::${r.error_type}`)) return true;
    if (includeRecovered || !r.recovered) hiddenSiteErrors += r.error_count ?? 0;
    return false;
  });
  return { pipelineRows, hiddenSiteErrors, outages };
}
